/**
 * CardOpeningAnimation (formerly EnvelopeAnimation / MailboxAnimation)
 *
 * A three-beat reveal shown before the card review modal:
 *   1. FRONT    the card front design slides in (closed folded card)
 *   2. INSIDE   the front cover swings open like a book, revealing the real
 *               printed message (greeting, body, closing, from line)
 *   3. ENVELOPE the card tucks into an envelope addressed to the real recipient,
 *               then it heads to the mailbox.
 *
 * Everything shown is the actual card: the chosen design, the user's real
 * message, and the recipient's real mailing address.
 *
 * Card is 216 × 166 px (matches CardDesignPreview size="md", the 6.5x5 folded
 * card aspect) so size never shifts between this animation and the review modal.
 */

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { DesignData } from './CardDesignPreview';
import CardDesignPreview from './CardDesignPreview';
import CardInsidePreview from './CardInsidePreview';

export const CARD_W = 216;
export const CARD_H = 166;  // 1.3:1 folded card, matches CardDesignPreview size="md"

export interface AnimationRecipient {
  name:    string;
  street?: string | null;
  city?:   string | null;
  state?:  string | null;
  postal?: string | null;
}

interface EnvelopeAnimationProps {
  design:       DesignData | null;
  contactName:  string;
  message?:     string;
  cardGreeting?: string | null;
  cardClosing?: string | null;
  cardFromLine?: string | null;
  recipient?:   AnimationRecipient | null;
  onComplete:   () => void;
}

// ── SVG Mailbox ───────────────────────────────────────────────────────────────

function MailboxBody() {
  return (
    <svg width="150" height="90" viewBox="0 0 180 108" fill="none" aria-hidden>
      <rect x="83" y="82" width="14" height="26" rx="3" fill="#7A6550"/>
      <rect x="8"  y="28" width="164" height="58" rx="14" fill="#4A6FA5"/>
      <ellipse cx="90" cy="28" rx="82" ry="12" fill="#5C82BE"/>
      <rect x="18" y="54" width="96" height="8" rx="4" fill="#2A4E7F" opacity="0.65"/>
      <rect x="164" y="32" width="5" height="52" rx="2.5" fill="#9CA3AF"/>
    </svg>
  );
}

// ── Envelope addressed to the real recipient ───────────────────────────────────

function AddressedEnvelope({ recipient, accentColor }: { recipient: AnimationRecipient; accentColor: string }) {
  const cityLine = [
    recipient.city,
    [recipient.state, recipient.postal].filter(Boolean).join(' '),
  ].filter(Boolean).join(', ');

  const line: CSSProperties = {
    fontFamily: '"Inter", Arial, sans-serif', color: '#3A332C', lineHeight: 1.45, fontSize: 11,
  };

  return (
    <div style={{
      width: 260, height: 172, borderRadius: 8, background: '#FBF7F0',
      boxShadow: '0 16px 50px rgba(0,0,0,0.40)', position: 'relative', overflow: 'hidden',
      border: '1px solid #EFE6D8',
    }}>
      {/* Flap */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 70,
        background: 'linear-gradient(#F4ECDD,#EFE4D2)',
        clipPath: 'polygon(0 0, 100% 0, 50% 100%)', opacity: 0.9,
        borderBottom: `1px solid ${accentColor}33`,
      }} aria-hidden />
      {/* Stamp */}
      <div style={{
        position: 'absolute', top: 12, right: 14, width: 34, height: 42, borderRadius: 2,
        background: '#FFFFFF', border: `1px dashed ${accentColor}`, display: 'flex',
        alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
      }}>
        <span style={{ fontSize: 18 }}>💌</span>
      </div>
      {/* Recipient address */}
      <div style={{ position: 'absolute', left: 30, top: 86 }}>
        <p style={{ ...line, fontWeight: 700, color: '#1A1410', marginBottom: 2 }}>{recipient.name}</p>
        {recipient.street && <p style={line}>{recipient.street}</p>}
        {cityLine && <p style={line}>{cityLine}</p>}
      </div>
      {/* Return address hint */}
      <p style={{
        position: 'absolute', top: 8, left: 12, fontSize: 7, color: '#B0AAA4',
        fontFamily: '"Inter", Arial, sans-serif', letterSpacing: '0.04em',
      }}>
        MomentSent
      </p>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function EnvelopeAnimation({
  design,
  contactName,
  message,
  cardGreeting,
  cardClosing,
  cardFromLine,
  recipient,
  onComplete,
}: EnvelopeAnimationProps) {
  const [slidIn,       setSlidIn]       = useState(false);
  const [opened,       setOpened]       = useState(false);   // cover swung open → inside
  const [showEnvelope, setShowEnvelope] = useState(false);
  const [mailed,       setMailed]       = useState(false);
  const [showSent,     setShowSent]     = useState(false);
  const [label,        setLabel]        = useState('');

  const ranRef = useRef(false);
  const accentColor = design?.accent_color ?? '#E8C12E';
  const hasAddress  = !!(recipient && (recipient.street || recipient.city));

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    requestAnimationFrame(() => setSlidIn(true));

    const timers = [
      setTimeout(() => setLabel('The front of your card'), 500),
      setTimeout(() => { setOpened(true); setLabel('Your message, printed inside'); }, 1500),
      setTimeout(() => { setShowEnvelope(true); setLabel(hasAddress ? `Addressed to ${contactName}` : `Ready for ${contactName}`); }, 3300),
      setTimeout(() => setMailed(true), 4500),
      setTimeout(() => setShowSent(true), 4900),
      setTimeout(onComplete, 5600),
    ];
    return () => timers.forEach(clearTimeout);
  }, [onComplete, contactName, hasAddress]);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-navy/75 backdrop-blur-md"
      style={{ animation: 'overlayFadeIn 0.4s ease both' }}
      onClick={onComplete}
      title="Click to skip"
    >
      <style>{`
        @keyframes overlayFadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes acLabel { from { opacity:0; transform:translateY(4px) } to { opacity:1; transform:translateY(0) } }
        @keyframes acSent  { from { opacity:0; transform:translateY(6px) } to { opacity:1; transform:translateY(0) } }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: 320, justifyContent: 'center' }}>

        {/* ── Stage: card (front + opening) OR envelope ────────────────────── */}
        <div style={{
          position: 'relative', width: CARD_W, height: CARD_H,
          transform: slidIn ? (mailed ? 'translateY(74px) scale(0.7)' : 'translateY(0) scale(1)') : 'translateY(-44px)',
          opacity:   slidIn ? (mailed ? 0 : 1) : 0,
          transition: mailed
            ? 'transform 0.55s cubic-bezier(0.4,0,1,1), opacity 0.55s ease'
            : 'transform 0.6s cubic-bezier(0.22,1,0.36,1), opacity 0.6s ease',
        }}>

          {/* Card (front cover + inside) — hidden once the envelope shows */}
          <div style={{
            position: 'absolute', inset: 0, perspective: 1000,
            opacity: showEnvelope ? 0 : 1,
            transition: 'opacity 0.4s ease',
            pointerEvents: 'none',
          }}>
            {/* Inside-right panel (revealed as the cover opens) */}
            <div style={{ position: 'absolute', inset: 0, borderRadius: 9, overflow: 'hidden' }}>
              <div style={{ width: CARD_W, height: CARD_H, overflow: 'hidden' }}>
                <CardInsidePreview
                  design={design ?? FALLBACK_DESIGN}
                  message={message ?? ''}
                  cardGreeting={cardGreeting ?? undefined}
                  cardClosing={cardClosing ?? undefined}
                  cardFromLine={cardFromLine ?? undefined}
                />
              </div>
            </div>

            {/* Front cover — hinged at the TOP edge, lifts up to open (landscape
                top-fold). Kept UNDER 90deg and faded out so the design never turns
                far enough to mirror (avoids the Chrome backface skew bug). */}
            <div style={{
              position: 'absolute', inset: 0,
              transformOrigin: 'center top',
              transform: opened ? 'rotateX(-82deg)' : 'rotateX(0deg)',
              opacity: opened ? 0 : 1,
              transition: opened
                ? 'transform 0.9s cubic-bezier(0.45,0,0.2,1), opacity 0.55s ease 0.4s'
                : 'none',
              boxShadow: opened ? 'none' : '0 14px 52px rgba(0,0,0,0.40)',
              borderRadius: 9, overflow: 'hidden', zIndex: 2,
            }}>
              {design
                ? <CardDesignPreview design={design} size="md" showName={false} />
                : <div style={{ width: CARD_W, height: CARD_H, borderRadius: 9, background: 'linear-gradient(135deg,#1A1410,#2d4a7a)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontSize: 44 }}>💌</span></div>}
            </div>
          </div>

          {/* Envelope addressed to the recipient */}
          <div style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: `translate(-50%,-50%) scale(${showEnvelope ? 1 : 0.85})`,
            opacity: showEnvelope ? 1 : 0,
            transition: 'opacity 0.45s ease, transform 0.5s cubic-bezier(0.22,1,0.36,1)',
          }}>
            {recipient
              ? <AddressedEnvelope recipient={recipient} accentColor={accentColor} />
              : <AddressedEnvelope recipient={{ name: contactName }} accentColor={accentColor} />}
          </div>
        </div>

        {/* ── Caption label ───────────────────────────────────────────────── */}
        <div style={{ height: 22, marginTop: 18, position: 'relative', width: 260, textAlign: 'center' }}>
          {label && !showSent && (
            <p key={label} style={{
              position: 'absolute', left: '50%', transform: 'translateX(-50%)',
              animation: 'acLabel 0.35s ease both',
              fontFamily: '"Inter", Arial, sans-serif', fontSize: 11, fontWeight: 600,
              letterSpacing: '0.14em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.55)', whiteSpace: 'nowrap',
            }}>
              {label}
            </p>
          )}
        </div>

        {/* ── Mailbox + "On its way!" ─────────────────────────────────────── */}
        <div style={{
          marginTop: 8, opacity: showEnvelope ? 1 : 0,
          transform: showEnvelope ? 'translateY(0)' : 'translateY(16px)',
          transition: 'opacity 0.5s ease, transform 0.5s cubic-bezier(0.22,1,0.36,1)',
        }}>
          <MailboxBody />
        </div>

        {showSent ? (
          <div style={{ marginTop: 16, textAlign: 'center', animation: 'acSent 0.45s ease both' }}>
            <p style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 22, fontWeight: 700, color: '#FFFFFF', marginBottom: 6 }}>
              On its way! 📬
            </p>
            <p style={{ fontFamily: '"Inter", Arial, sans-serif', fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
              For {contactName} · review the message before it ships
            </p>
          </div>
        ) : (
          <p style={{ marginTop: 18, fontFamily: '"Inter", Arial, sans-serif', fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
            Tap anywhere to skip →
          </p>
        )}
      </div>
    </div>
  );
}

// Minimal fallback so CardInsidePreview always has a design (color only matters).
const FALLBACK_DESIGN = {
  id: 'fallback', name: '', heading: '', tagline: '', style: 'modern',
  bg_color: '#FEFEFE', bg_type: 'solid', pattern: 'none', pattern_color: '#7A9E7E',
  accent_color: '#7A9E7E', text_color: '#1A1410', emoji: '',
} as DesignData;
